import { useState, useEffect } from 'react';
import { apiCall } from '../../config/api';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

interface HealthFund {
  id: string;
  name: string;
  code: string;
  has_state_variations: boolean;
}

interface State {
  id: string;
  code: string;
  name: string;
}

export const HealthFundScheduleUpload: React.FC = () => {
  const [healthFunds, setHealthFunds] = useState<HealthFund[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [versionCode, setVersionCode] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [taskId, setTaskId] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    fetchHealthFunds();
    fetchStates();
  }, []);

  useEffect(() => {
    if (taskId) {
      const interval = setInterval(() => pollTaskStatus(), 1000);
      return () => clearInterval(interval);
    }
  }, [taskId]);

  const fetchHealthFunds = async () => {
    try {
      const response = await apiCall('/billing/health-funds/?is_active=true');
      if (response.ok) {
        const data = await response.json();
        setHealthFunds(data.results || data || []);
      }
    } catch (error) {
      showToast('Failed to load health funds', 'error');
    }
  };

  const fetchStates = async () => {
    try {
      const response = await apiCall('/billing/australian-states/');
      if (response.ok) {
        const data = await response.json();
        setStates(data.results || data || []);
      }
    } catch (error) {
      showToast('Failed to load states', 'error');
    }
  };

  const pollTaskStatus = async () => {
    if (!taskId) return;

    try {
      const response = await apiCall(`/billing/mbs-items/task_status/?task_id=${taskId}`);
      if (!response.ok) return;
      const data = await response.json();
      const { status, progress: taskProgress, result } = data;

      if (status === 'PROGRESS' && taskProgress) {
        setProgress(taskProgress.percent || 0);
        setProgressMessage(taskProgress.message || 'Processing...');
      } else if (status === 'SUCCESS') {
        setProgress(100);
        setProgressMessage('Upload complete!');
        setTaskId('');
        setUploading(false);
        
        const { items_created, items_updated, schedule_created } = result;
        showToast(
          `Schedule ${schedule_created ? 'created' : 'updated'}: ${items_created} items created, ${items_updated} items updated`,
          'success'
        );
        
        // Reset form
        setFile(null);
        setProgress(0);
        setProgressMessage('');
      } else if (status === 'FAILURE') {
        setTaskId('');
        setUploading(false);
        setProgress(0);
        showToast('Upload failed: ' + (result?.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Failed to poll task status:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file || !selectedFund || !versionCode || !effectiveDate) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const selectedFundData = healthFunds.find(f => f.id === selectedFund);
    if (selectedFundData?.has_state_variations && !selectedState) {
      showToast('Please select a state (this fund has state variations)', 'error');
      return;
    }

    setUploading(true);
    setProgress(0);
    setProgressMessage('Uploading file...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('health_fund_id', selectedFund);
      formData.append('version_code', versionCode);
      formData.append('effective_date', effectiveDate);
      if (selectedState) {
        formData.append('state_code', selectedState);
      }

      // Determine endpoint based on file type
      const endpoint = file.name.toLowerCase().endsWith('.xml')
        ? '/billing/health-fund-schedules/upload_dva/'
        : '/billing/health-fund-schedules/upload_csv/';

      const response = await apiCall(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setTaskId(data.task_id);
        showToast('Upload started...', 'info');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      setUploading(false);
      showToast(error.response?.data?.error || 'Upload failed', 'error');
    }
  };

  const selectedFundData = healthFunds.find(f => f.id === selectedFund);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Upload Health Fund Schedule</h1>

        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <form onSubmit={handleUpload} className="space-y-6">
              {/* Health Fund Selection */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Health Fund *</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={selectedFund}
                  onChange={(e) => setSelectedFund(e.target.value)}
                  disabled={uploading}
                  required
                >
                  <option value="">Select a health fund</option>
                  {healthFunds.map((fund) => (
                    <option key={fund.id} value={fund.id}>
                      {fund.name} ({fund.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* State Selection (conditional) */}
              {selectedFundData?.has_state_variations && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">State/Territory *</span>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    disabled={uploading}
                    required
                  >
                    <option value="">Select a state</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.code}>
                        {state.name} ({state.code})
                      </option>
                    ))}
                  </select>
                  <label className="label">
                    <span className="label-text-alt text-info">
                      This fund has state-based variations
                    </span>
                  </label>
                </div>
              )}

              {/* Version Code */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Version Code *</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g., 20251101 or NOV2025"
                  value={versionCode}
                  onChange={(e) => setVersionCode(e.target.value)}
                  disabled={uploading}
                  required
                />
                <label className="label">
                  <span className="label-text-alt">Version identifier for this schedule</span>
                </label>
              </div>

              {/* Effective Date */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Effective Date *</span>
                </label>
                <input
                  type="date"
                  className="input input-bordered w-full"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  disabled={uploading}
                  required
                />
                <label className="label">
                  <span className="label-text-alt">Date when this schedule becomes effective</span>
                </label>
              </div>

              {/* File Upload */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Schedule File *</span>
                </label>
                <input
                  type="file"
                  className="file-input file-input-bordered w-full"
                  accept=".xml,.csv"
                  onChange={handleFileChange}
                  disabled={uploading}
                  required
                />
                <label className="label">
                  <span className="label-text-alt">
                    Supported formats: XML (DVA), CSV (BUPA, St Lukes, GU Health, Medibank, NIB)
                  </span>
                </label>
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{progressMessage}</span>
                    <span>{progress}%</span>
                  </div>
                  <progress
                    className="progress progress-primary w-full"
                    value={progress}
                    max="100"
                  ></progress>
                </div>
              )}

              {/* Submit Button */}
              <div className="form-control mt-6">
                <button
                  type="submit"
                  className={`btn btn-primary ${uploading ? 'loading' : ''}`}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Information Card */}
        <div className="alert alert-info mt-6">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div>
            <h3 className="font-bold">Upload Guidelines</h3>
            <ul className="text-sm mt-2 space-y-1">
              <li>• DVA schedules: Use XML format</li>
              <li>• Private funds (BUPA, St Lukes, etc.): Use CSV format</li>
              <li>• Version code typically matches the effective date (e.g., 20251101)</li>
              <li>• Schedules are typically updated semi-annually (1 July, 1 November)</li>
              <li>• GU Health requires state selection due to state-based variations</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />
    </div>
  );
};
